import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../api';
import { computeMatchPercent } from '../utils/skillMatch';
import { sortByEntityId } from '../utils/sortById';
import { useAuth } from '../hooks/useAuth';
import StatCard from '../components/StatCard';
import InsightModal from '../components/InsightModal';
import {
  DashboardPage,
  DashboardHeader,
  DashboardInsightsToggle,
  DashboardMetrics,
  DashboardMetricGrid,
  DashboardPanelsGrid,
  DashboardPanel
} from '../components/DashboardLayout';
import DashboardInsights from '../components/DashboardInsights';
import {
  candidateColumns,
  jobColumns,
  applicationColumns,
  clientColumns,
  recruiterColumns,
  invoiceColumns,
  mapCandidates,
  mapJobs,
  mapApplications,
  mapClients,
  mapRecruiters,
  mapInvoices
} from '../utils/dashboardColumns';
import { isJobActivelyHiring, getJobStatusDistribution, formatJobStatusLabel } from '../utils/jobStatus';

function maskCompanyName(name) {
  const raw = String(name || '').trim();
  if (!raw) return '—';
  const parts = raw.split(/\s+/).filter(Boolean);
  return parts
    .map(part => {
      if (part.length <= 2) return `${part[0] || ''}x`;
      return `${part[0]}${'x'.repeat(Math.max(1, part.length - 2))}${part[part.length - 1]}`;
    })
    .join(' ');
}

export default function Dashboard() {
  const { user } = useAuth();
  
  // Admin Dashboard - All insights
  if (user?.role === 'admin') {
    return <AdminDashboard />;
  }
  
  // Recruiter Dashboard - Performance stats
  if (user?.role === 'recruiter') {
    return <RecruiterDashboard />;
  }
  
  // Client Dashboard - Their jobs and submissions
  if (user?.role === 'client') {
    return <ClientDashboard />;
  }
  
  // Candidate Dashboard - Their applications
  if (user?.role === 'candidate') {
    return <CandidateDashboard />;
  }
  
  return <div className="p-6">Loading...</div>;
}

function AdminDashboard() {
  const [insight, setInsight] = useState(null);
  const [view, setView] = useState('overview');
  const { data: candidates } = useQuery({ queryKey: ['candidates'], queryFn: () => api.get('/candidates').then(r => r.data) });
  const { data: jobs } = useQuery({ queryKey: ['jobs'], queryFn: () => api.get('/jobs').then(r => r.data) });
  const { data: applications } = useQuery({ queryKey: ['applications'], queryFn: () => api.get('/applications').then(r => r.data) });
  const { data: clients } = useQuery({ queryKey: ['clients'], queryFn: () => api.get('/clients').then(r => r.data) });
  const { data: recruiters } = useQuery({ queryKey: ['recruiters'], queryFn: () => api.get('/recruiters').then(r => r.data).catch(() => []) });
  const { data: invoices } = useQuery({ queryKey: ['invoices'], queryFn: () => api.get('/invoices').then(r => r.data) });

  // Calculate positions and balance insights
  const totalPositions = jobs?.reduce((sum, j) => sum + (j.numberOfPositions || 1), 0) || 0;
  const totalJoined = applications?.filter(a => a.stage === 'joined').length || 0;
  const totalBalanceToRecruit = jobs?.reduce((sum, j) => {
    const positions = j.numberOfPositions || 1;
    const joined = applications?.filter(a => a.jobId === j.id && a.stage === 'joined').length || 0;
    return sum + Math.max(0, positions - joined);
  }, 0) || 0;
  const jobsWithMultiplePositions = jobs?.filter(j => (j.numberOfPositions || 1) > 1).length || 0;

  const stats = {
    totalCandidates: candidates?.length || 0,
    totalJobs: jobs?.length || 0,
    totalApplications: applications?.length || 0,
    totalClients: clients?.length || 0,
    totalRecruiters: recruiters?.length || 0,
    totalInvoices: invoices?.length || 0,
    activeJobs: jobs?.filter(isJobActivelyHiring).length || 0,
    closedJobs: jobs?.filter(j => j.status === 'closed').length || 0,
    submittedApps: applications?.filter(a => a.stage === 'submitted').length || 0,
    selectedApps: applications?.filter(a => a.stage === 'selected').length || 0,
    notSelectedApps: applications?.filter(a => a.stage === 'not_selected').length || 0,
    inInterviewApps: applications?.filter(a => a.stage === 'interview').length || 0,
    shortlistedApps: applications?.filter(a => a.stage === 'shortlisted').length || 0,
    offerApps: applications?.filter(a => a.stage === 'offer').length || 0,
    joinedApps: totalJoined,
    rejectedApps: applications?.filter(a => a.stage === 'rejected').length || 0,
    paidInvoices: invoices?.filter(i => i.status === 'paid').length || 0,
    pendingInvoices: invoices?.filter(i => i.status === 'sent').length || 0,
    totalPositions: totalPositions,
    totalBalanceToRecruit: totalBalanceToRecruit,
    jobsWithMultiplePositions: jobsWithMultiplePositions
  };

  // Stage distribution
  const stageData = {
    submitted: applications?.filter(a => a.stage === 'submitted').length || 0,
    selected: applications?.filter(a => a.stage === 'selected').length || 0,
    not_selected: applications?.filter(a => a.stage === 'not_selected').length || 0,
    interview: applications?.filter(a => a.stage === 'interview').length || 0,
    shortlisted: applications?.filter(a => a.stage === 'shortlisted').length || 0,
    offer: applications?.filter(a => a.stage === 'offer').length || 0,
    joined: applications?.filter(a => a.stage === 'joined').length || 0,
    rejected: applications?.filter(a => a.stage === 'rejected').length || 0
  };

  const jobStatusData = getJobStatusDistribution(jobs || []);
  const activelyHiringJobs = (jobs || []).filter(isJobActivelyHiring);

  const openInsight = (title, filename, columns, rows, subtitle) =>
    setInsight({ title, filename, columns, rows, subtitle });

  const toggleInsights = () => setView(v => (v === 'insights' ? 'overview' : 'insights'));

  return (
    <DashboardPage>
      <InsightModal insight={insight} onClose={() => setInsight(null)} />
      <DashboardHeader
        title="Admin Dashboard"
        subtitle={
          view === 'insights'
            ? 'Hiring analytics — click metrics or charts for details and Excel export'
            : 'Click any metric card to view details and export to Excel'
        }
        titleBadge={
          <DashboardInsightsToggle active={view === 'insights'} onToggle={toggleInsights} />
        }
      />

      {view === 'insights' ? (
        <DashboardInsights
          role="admin"
          applications={applications || []}
          jobs={jobs || []}
          candidates={candidates || []}
          clients={clients || []}
          recruiters={recruiters || []}
          invoices={invoices || []}
          onOpenInsight={setInsight}
        />
      ) : (
      <>
      <DashboardMetrics>
        <div className="space-y-2">
          <div>
            <div className="text-[11px] font-semibold text-[hsl(var(--ds-muted-fg))] uppercase tracking-wide mb-1">
              Core Overview
            </div>
            <DashboardMetricGrid>
              <StatCard label="Candidates" value={stats.totalCandidates} color="blue"
                onClick={() => openInsight('All Candidates', 'candidates', candidateColumns, mapCandidates(candidates))} />
              <StatCard label="Jobs" value={stats.totalJobs} color="green"
                onClick={() => openInsight('All Jobs', 'jobs', jobColumns, mapJobs(jobs))} />
              <StatCard label="Active Jobs" value={stats.activeJobs} color="emerald"
                onClick={() => openInsight('Actively Hiring Jobs', 'active_jobs', jobColumns, mapJobs(activelyHiringJobs))} />
              <StatCard label="Applications" value={stats.totalApplications} color="purple"
                onClick={() => openInsight('All Applications', 'applications', applicationColumns, mapApplications(applications))} />
              <StatCard label="Hiring Companies" value={stats.totalClients} color="orange"
                onClick={() => openInsight('Hiring Companies', 'clients', clientColumns, mapClients(clients))} />
              <StatCard label="Recruiters" value={stats.totalRecruiters} color="indigo"
                onClick={() => openInsight('All Recruiters', 'recruiters', recruiterColumns, mapRecruiters(recruiters))} />
            </DashboardMetricGrid>
          </div>

          <div>
            <div className="text-[11px] font-semibold text-[hsl(var(--ds-muted-fg))] uppercase tracking-wide mb-1">
              Application Outcomes
            </div>
            <DashboardMetricGrid>
              <StatCard label="Submitted" value={stats.submittedApps} color="blue"
                onClick={() => openInsight('Submitted Applications', 'submitted', applicationColumns, mapApplications(applications?.filter(a => a.stage === 'submitted')))} />
              <StatCard label="Selected" value={stats.selectedApps} color="cyan"
                onClick={() => openInsight('Selected Applications', 'selected', applicationColumns, mapApplications(applications?.filter(a => a.stage === 'selected')))} />
              <StatCard label="Not Selected" value={stats.notSelectedApps} color="rose"
                onClick={() => openInsight('Not Selected Applications', 'not_selected', applicationColumns, mapApplications(applications?.filter(a => a.stage === 'not_selected')))} />
              <StatCard label="In Interview" value={stats.inInterviewApps} color="yellow"
                onClick={() => openInsight('Interview Stage', 'interview', applicationColumns, mapApplications(applications?.filter(a => a.stage === 'interview')))} />
              <StatCard label="Shortlisted" value={stats.shortlistedApps} color="teal"
                onClick={() => openInsight('Shortlisted Applications', 'shortlisted', applicationColumns, mapApplications(applications?.filter(a => a.stage === 'shortlisted')))} />
              <StatCard label="Offers" value={stats.offerApps} color="purple"
                onClick={() => openInsight('Offer Stage', 'offers', applicationColumns, mapApplications(applications?.filter(a => a.stage === 'offer')))} />
              <StatCard label="Joined" value={stats.joinedApps} color="emerald"
                onClick={() => openInsight('Joined Applications', 'joined', applicationColumns, mapApplications(applications?.filter(a => a.stage === 'joined')))} />
              <StatCard label="Rejected" value={stats.rejectedApps} color="red"
                onClick={() => openInsight('Rejected Applications', 'rejected', applicationColumns, mapApplications(applications?.filter(a => a.stage === 'rejected')))} />
            </DashboardMetricGrid>
          </div>

          <div>
            <div className="text-[11px] font-semibold text-[hsl(var(--ds-muted-fg))] uppercase tracking-wide mb-1">
              Billing
            </div>
            <DashboardMetricGrid>
              <StatCard label="Paid Invoices" value={stats.paidInvoices} color="green"
                onClick={() => openInsight('Paid Invoices', 'invoices_paid', invoiceColumns, mapInvoices(invoices?.filter(i => i.status === 'paid'), clients))} />
              <StatCard label="Pending Invoices" value={stats.pendingInvoices} color="amber"
                onClick={() => openInsight('Pending Invoices', 'invoices_pending', invoiceColumns, mapInvoices(invoices?.filter(i => i.status === 'sent'), clients))} />
            </DashboardMetricGrid>
          </div>

          <div>
            <div className="text-[11px] font-semibold text-[hsl(var(--ds-muted-fg))] uppercase tracking-wide mb-1">
              Hiring Capacity
            </div>
            <DashboardMetricGrid>
              <StatCard label="Positions" value={stats.totalPositions} color="blue"
                onClick={() => openInsight('Jobs with Positions', 'jobs_positions', jobColumns, mapJobs(jobs))} />
              <StatCard label="Balance" value={stats.totalBalanceToRecruit} color="orange"
                onClick={() => openInsight('Open Headcount by Job', 'balance', jobColumns, mapJobs(jobs?.filter(j => (j.balanceToRecruit || 0) > 0)))} />
              <StatCard label="Multi-Pos Jobs" value={stats.jobsWithMultiplePositions} color="indigo"
                onClick={() => openInsight('Multi-Position Jobs', 'multi_jobs', jobColumns, mapJobs(jobs?.filter(j => (j.numberOfPositions || 1) > 1)))} />
            </DashboardMetricGrid>
          </div>
        </div>
      </DashboardMetrics>

      <DashboardPanelsGrid>
        <DashboardPanel title="Application Stages">
          <BarChart data={stageData} colors={{ submitted: 'bg-[hsl(var(--ds-primary))]', selected: 'bg-cyan-500', not_selected: 'bg-rose-500', interview: 'bg-yellow-500', shortlisted: 'bg-[hsl(var(--ds-primary)/0.75)]', offer: 'bg-purple-500', joined: 'bg-green-500', rejected: 'bg-red-500' }} />
        </DashboardPanel>

        <DashboardPanel title="Job Status Distribution" subtitle="Auto-updated from ATS & assignments">
          <BarChart
            data={jobStatusData}
            labelFormat={formatJobStatusLabel}
            colors={{ open: 'bg-green-500', 'in-progress': 'bg-[hsl(var(--ds-primary))]', 'on-hold': 'bg-yellow-500', closed: 'bg-[hsl(var(--ds-muted))]0', inactive: 'bg-red-500' }}
          />
        </DashboardPanel>

        <DashboardPanel title="Recruiter Performance">
          <div className="space-y-3">
          {recruiters && recruiters.length > 0 ? (
            sortByEntityId(recruiters).map(recruiter => {
              const recruiterJobs = jobs?.filter(j => j.recruiterId === recruiter.id) || [];
              const recruiterApps = applications?.filter(a => {
                const appJob = jobs?.find(j => j.id === a.jobId);
                return appJob?.recruiterId === recruiter.id;
              }) || [];
              const joined = recruiterApps.filter(a => a.stage === 'joined').length;
              const successRate = recruiterApps.length > 0 ? ((joined / recruiterApps.length) * 100).toFixed(1) : 0;
              return (
                <div key={recruiter.id} className="border border-[hsl(var(--ds-border))] p-2 bg-[hsl(var(--ds-muted))] text-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="font-semibold">{recruiter.name}</div>
                      <div className="text-xs text-[hsl(var(--ds-muted-fg))]">{recruiter.email}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-green-600">{successRate}%</div>
                      <div className="text-xs text-[hsl(var(--ds-muted-fg))]">Success Rate</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-xs mt-2">
                    <div>
                      <div className="text-[hsl(var(--ds-muted-fg))]">Jobs</div>
                      <div className="font-semibold">{recruiterJobs.length}</div>
                    </div>
                    <div>
                      <div className="text-[hsl(var(--ds-muted-fg))]">Submissions</div>
                      <div className="font-semibold">{recruiterApps.length}</div>
                    </div>
                    <div>
                      <div className="text-[hsl(var(--ds-muted-fg))]">Joined</div>
                      <div className="font-semibold text-green-600">{joined}</div>
                    </div>
                    <div>
                      <div className="text-[hsl(var(--ds-muted-fg))]">In Progress</div>
                      <div className="font-semibold">{recruiterApps.filter(a => ['submitted', 'selected', 'interview', 'shortlisted', 'offer'].includes(a.stage)).length}</div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-[hsl(var(--ds-muted-fg))] text-sm">No recruiters found</div>
          )}
          </div>
        </DashboardPanel>

        <DashboardPanel title="Recent Applications">
          <div className="space-y-2">
          {sortByEntityId(applications || []).slice(0, 10).map(app => (
            <div key={app.id} className="flex items-center justify-between py-1.5 border-b border-[hsl(var(--ds-border))] text-xs">
              <div className="min-w-0 pr-2">
                <span className="font-medium">{app.candidateName || app.candidateId}</span>
                <span className="text-[hsl(var(--ds-muted-fg))] ml-2">→ {app.jobTitle || app.jobId}</span>
              </div>
              <span className={`px-2 py-1 text-xs shrink-0 ${getStageColor(app.stage)}`}>{app.stage}</span>
            </div>
          ))}
          {(!applications || applications.length === 0) && (
            <div className="text-[hsl(var(--ds-muted-fg))] text-sm">No recent applications</div>
          )}
          </div>
        </DashboardPanel>
      </DashboardPanelsGrid>
      </>
      )}
    </DashboardPage>
  );
}

function RecruiterDashboard() {
  const [insight, setInsight] = useState(null);
  const [view, setView] = useState('overview');
  const { data: jobs } = useQuery({ queryKey: ['jobs'], queryFn: () => api.get('/jobs').then(r => r.data) });
  const { data: applications } = useQuery({ queryKey: ['applications'], queryFn: () => api.get('/applications').then(r => r.data) });
  const { data: candidates } = useQuery({ queryKey: ['candidates'], queryFn: () => api.get('/candidates').then(r => r.data) });
  const { data: recruiters } = useQuery({ queryKey: ['recruiters'], queryFn: () => api.get('/recruiters').then(r => r.data).catch(() => []) });

  // API already scopes jobs/applications to the logged-in recruiter
  const myJobs = jobs || [];
  const myApplications = applications || [];

  // Calculate positions and balance for recruiter
  const myTotalPositions = myJobs.reduce((sum, j) => sum + (j.numberOfPositions || 1), 0);
  const myTotalJoined = myApplications.filter(a => a.stage === 'joined').length;
  const myBalanceToRecruit = myJobs.reduce((sum, j) => {
    const positions = j.numberOfPositions || 1;
    const joined = myApplications.filter(a => a.jobId === j.id && a.stage === 'joined').length;
    return sum + Math.max(0, positions - joined);
  }, 0);
  const myJobsWithMultiplePositions = myJobs.filter(j => (j.numberOfPositions || 1) > 1).length;

  const stats = {
    assignedJobs: myJobs.length,
    activeJobs: myJobs.filter(isJobActivelyHiring).length,
    totalSubmissions: myApplications.length,
    submitted: myApplications.filter(a => a.stage === 'submitted').length,
    selected: myApplications.filter(a => a.stage === 'selected').length,
    notSelected: myApplications.filter(a => a.stage === 'not_selected').length,
    joined: myTotalJoined,
    inInterview: myApplications.filter(a => a.stage === 'interview').length,
    shortlisted: myApplications.filter(a => a.stage === 'shortlisted').length,
    offers: myApplications.filter(a => a.stage === 'offer').length,
    rejected: myApplications.filter(a => a.stage === 'rejected').length,
    successRate: myApplications.length > 0 ? ((myTotalJoined / myApplications.length) * 100).toFixed(1) : 0,
    totalPositions: myTotalPositions,
    balanceToRecruit: myBalanceToRecruit,
    jobsWithMultiplePositions: myJobsWithMultiplePositions
  };

  const stageData = {
    submitted: myApplications.filter(a => a.stage === 'submitted').length,
    selected: myApplications.filter(a => a.stage === 'selected').length,
    not_selected: myApplications.filter(a => a.stage === 'not_selected').length,
    interview: myApplications.filter(a => a.stage === 'interview').length,
    shortlisted: myApplications.filter(a => a.stage === 'shortlisted').length,
    offer: myApplications.filter(a => a.stage === 'offer').length,
    joined: myApplications.filter(a => a.stage === 'joined').length,
    rejected: myApplications.filter(a => a.stage === 'rejected').length
  };

  const openInsight = (title, filename, columns, rows) =>
    setInsight({ title, filename, columns, rows });

  const toggleInsights = () => setView(v => (v === 'insights' ? 'overview' : 'insights'));

  return (
    <DashboardPage>
      <InsightModal insight={insight} onClose={() => setInsight(null)} />
      <DashboardHeader
        title="Recruiter Dashboard"
        subtitle={
          view === 'insights'
            ? 'Your hiring analytics — scoped to your assigned jobs'
            : 'Click metrics to drill down and export'
        }
        titleBadge={
          <DashboardInsightsToggle active={view === 'insights'} onToggle={toggleInsights} />
        }
      />

      {view === 'insights' ? (
        <DashboardInsights
          role="recruiter"
          applications={myApplications}
          jobs={myJobs}
          candidates={candidates || []}
          recruiters={recruiters || []}
          onOpenInsight={setInsight}
        />
      ) : (
      <>
      <DashboardMetrics>
        <div className="space-y-2">
          <div>
            <div className="text-[11px] font-semibold text-[hsl(var(--ds-muted-fg))] uppercase tracking-wide mb-1">
              Job Overview
            </div>
            <DashboardMetricGrid>
              <StatCard label="Assigned Jobs" value={stats.assignedJobs} color="blue"
                onClick={() => openInsight('My Jobs', 'my_jobs', jobColumns, mapJobs(myJobs))} />
              <StatCard label="Active Jobs" value={stats.activeJobs} color="green"
                onClick={() => openInsight('Active Jobs', 'my_active_jobs', jobColumns, mapJobs(myJobs.filter(isJobActivelyHiring)))} />
              <StatCard label="Positions" value={stats.totalPositions} color="blue"
                onClick={() => openInsight('My Positions', 'positions', jobColumns, mapJobs(myJobs))} />
              <StatCard label="Balance" value={stats.balanceToRecruit} color="orange"
                onClick={() => openInsight('Balance to Recruit', 'balance', jobColumns, mapJobs(myJobs.filter(j => (j.balanceToRecruit || 0) > 0)))} />
              <StatCard label="Multi-Pos" value={stats.jobsWithMultiplePositions} color="indigo"
                onClick={() => openInsight('Multi-Position Jobs', 'multi', jobColumns, mapJobs(myJobs.filter(j => (j.numberOfPositions || 1) > 1)))} />
            </DashboardMetricGrid>
          </div>

          <div>
            <div className="text-[11px] font-semibold text-[hsl(var(--ds-muted-fg))] uppercase tracking-wide mb-1">
              ATS Pipeline
            </div>
            <DashboardMetricGrid>
              <StatCard label="Submissions" value={stats.totalSubmissions} color="purple"
                onClick={() => openInsight('My Submissions', 'my_applications', applicationColumns, mapApplications(myApplications))} />
              <StatCard label="Submitted" value={stats.submitted} color="blue"
                onClick={() => openInsight('Submitted', 'submitted', applicationColumns, mapApplications(myApplications.filter(a => a.stage === 'submitted')))} />
              <StatCard label="Selected" value={stats.selected} color="cyan"
                onClick={() => openInsight('Selected', 'selected', applicationColumns, mapApplications(myApplications.filter(a => a.stage === 'selected')))} />
              <StatCard label="Not Selected" value={stats.notSelected} color="rose"
                onClick={() => openInsight('Not Selected', 'not_selected', applicationColumns, mapApplications(myApplications.filter(a => a.stage === 'not_selected')))} />
              <StatCard label="In Interview" value={stats.inInterview} color="yellow"
                onClick={() => openInsight('In Interview', 'interview', applicationColumns, mapApplications(myApplications.filter(a => a.stage === 'interview')))} />
              <StatCard label="Shortlisted" value={stats.shortlisted} color="teal"
                onClick={() => openInsight('Shortlisted', 'shortlisted', applicationColumns, mapApplications(myApplications.filter(a => a.stage === 'shortlisted')))} />
              <StatCard label="Offers" value={stats.offers} color="purple"
                onClick={() => openInsight('Offers', 'offer', applicationColumns, mapApplications(myApplications.filter(a => a.stage === 'offer')))} />
              <StatCard label="Joined" value={stats.joined} color="green"
                onClick={() => openInsight('Joined', 'joined', applicationColumns, mapApplications(myApplications.filter(a => a.stage === 'joined')))} />
              <StatCard label="Rejected" value={stats.rejected} color="red"
                onClick={() => openInsight('Rejected', 'rejected', applicationColumns, mapApplications(myApplications.filter(a => a.stage === 'rejected')))} />
              <StatCard label="Success Rate" value={`${stats.successRate}%`} color="emerald" />
            </DashboardMetricGrid>
          </div>
        </div>
      </DashboardMetrics>

      <DashboardPanelsGrid columns={2}>
        <DashboardPanel title="My Application Pipeline">
          <BarChart data={stageData} colors={{ submitted: 'bg-[hsl(var(--ds-primary))]', selected: 'bg-cyan-500', not_selected: 'bg-rose-500', interview: 'bg-yellow-500', shortlisted: 'bg-[hsl(var(--ds-primary)/0.75)]', offer: 'bg-purple-500', joined: 'bg-green-500', rejected: 'bg-red-500' }} />
        </DashboardPanel>

        <DashboardPanel title="My Active Jobs">
          <div className="space-y-2">
            {myJobs.filter(isJobActivelyHiring).length > 0 ? (
              sortByEntityId(myJobs.filter(isJobActivelyHiring)).map(job => (
              <div key={job.id} className="flex items-center justify-between p-2 bg-[hsl(var(--ds-muted))] border border-[hsl(var(--ds-border))]">
                <div className="min-w-0 pr-2">
                  <div className="font-medium text-sm">{job.title}</div>
                  <div className="text-xs text-[hsl(var(--ds-muted-fg))]">{job.clientName || job.clientId}</div>
                </div>
                <span className="px-2 py-1 text-xs shrink-0 bg-green-100 text-green-700">{job.status}</span>
              </div>
            ))
            ) : (
              <div className="text-[hsl(var(--ds-muted-fg))] text-sm">No active jobs</div>
            )}
          </div>
        </DashboardPanel>
      </DashboardPanelsGrid>
      </>
      )}
    </DashboardPage>
  );
}

function ClientDashboard() {
  const [insight, setInsight] = useState(null);
  const [view, setView] = useState('overview');
  const { data: candidates } = useQuery({ queryKey: ['candidates'], queryFn: () => api.get('/candidates').then(r => r.data) });
  const { data: jobs } = useQuery({ queryKey: ['jobs'], queryFn: () => api.get('/jobs').then(r => r.data) });
  const { data: applications } = useQuery({ queryKey: ['applications'], queryFn: () => api.get('/applications').then(r => r.data) });
  const { data: invoices } = useQuery({ queryKey: ['invoices'], queryFn: () => api.get('/invoices').then(r => r.data) });

  // API already scopes jobs, applications, and invoices to the logged-in client
  const myJobs = jobs || [];
  const myApplications = applications || [];
  const myInvoices = invoices || [];

  // Calculate positions and balance for client
  const myTotalPositions = myJobs.reduce((sum, j) => sum + (j.numberOfPositions || 1), 0);
  const myTotalJoined = myApplications.filter(a => a.stage === 'joined').length;
  const myBalanceToRecruit = myJobs.reduce((sum, j) => {
    const positions = j.numberOfPositions || 1;
    const joined = myApplications.filter(a => a.jobId === j.id && a.stage === 'joined').length || 0;
    return sum + Math.max(0, positions - joined);
  }, 0);
  const myJobsWithMultiplePositions = myJobs.filter(j => (j.numberOfPositions || 1) > 1).length;

  const stats = {
    totalJobs: myJobs.length,
    activeJobs: myJobs.filter(isJobActivelyHiring).length,
    totalSubmissions: myApplications.length,
    submitted: myApplications.filter(a => a.stage === 'submitted').length,
    selected: myApplications.filter(a => a.stage === 'selected').length,
    notSelected: myApplications.filter(a => a.stage === 'not_selected').length,
    inInterview: myApplications.filter(a => a.stage === 'interview').length,
    shortlisted: myApplications.filter(a => a.stage === 'shortlisted').length,
    offers: myApplications.filter(a => a.stage === 'offer').length,
    joined: myApplications.filter(a => a.stage === 'joined').length,
    rejected: myApplications.filter(a => a.stage === 'rejected').length,
    totalInvoices: myInvoices.length,
    paidInvoices: myInvoices.filter(i => i.status === 'paid').length,
    pendingInvoices: myInvoices.filter(i => i.status === 'sent').length,
    totalPositions: myTotalPositions,
    balanceToRecruit: myBalanceToRecruit,
    jobsWithMultiplePositions: myJobsWithMultiplePositions
  };

  const stageData = {
    submitted: myApplications.filter(a => a.stage === 'submitted').length,
    selected: myApplications.filter(a => a.stage === 'selected').length,
    not_selected: myApplications.filter(a => a.stage === 'not_selected').length,
    interview: myApplications.filter(a => a.stage === 'interview').length,
    shortlisted: myApplications.filter(a => a.stage === 'shortlisted').length,
    offer: myApplications.filter(a => a.stage === 'offer').length,
    joined: myApplications.filter(a => a.stage === 'joined').length,
    rejected: myApplications.filter(a => a.stage === 'rejected').length
  };

  const openInsight = (title, filename, columns, rows) =>
    setInsight({ title, filename, columns, rows });

  const toggleInsights = () => setView(v => (v === 'insights' ? 'overview' : 'insights'));

  return (
    <DashboardPage>
      <InsightModal insight={insight} onClose={() => setInsight(null)} />
      <DashboardHeader
        title="Client Dashboard"
        subtitle={
          view === 'insights'
            ? 'Your hiring outcomes and candidate demographics'
            : 'Click metrics to drill down and export'
        }
        titleBadge={
          <DashboardInsightsToggle active={view === 'insights'} onToggle={toggleInsights} />
        }
      />

      {view === 'insights' ? (
        <DashboardInsights
          role="client"
          applications={myApplications}
          jobs={myJobs}
          candidates={candidates || []}
          invoices={myInvoices}
          onOpenInsight={setInsight}
        />
      ) : (
      <>
      <DashboardMetrics>
        <div className="space-y-2">
          <div>
            <div className="text-[11px] font-semibold text-[hsl(var(--ds-muted-fg))] uppercase tracking-wide mb-1">
              Job Overview
            </div>
            <DashboardMetricGrid>
              <StatCard label="My Jobs" value={stats.totalJobs} color="blue"
                onClick={() => openInsight('My Jobs', 'client_jobs', jobColumns, mapJobs(myJobs))} />
              <StatCard label="Active Jobs" value={stats.activeJobs} color="green"
                onClick={() => openInsight('Active Jobs', 'client_active', jobColumns, mapJobs(myJobs.filter(isJobActivelyHiring)))} />
              <StatCard label="Positions" value={stats.totalPositions} color="blue"
                onClick={() => openInsight('Positions', 'positions', jobColumns, mapJobs(myJobs))} />
              <StatCard label="Balance" value={stats.balanceToRecruit} color="orange"
                onClick={() => openInsight('Open Headcount', 'balance', jobColumns, mapJobs(myJobs.filter(j => (j.balanceToRecruit || 0) > 0)))} />
              <StatCard label="Multi-Pos" value={stats.jobsWithMultiplePositions} color="indigo"
                onClick={() => openInsight('Multi-Position Jobs', 'multi', jobColumns, mapJobs(myJobs.filter(j => (j.numberOfPositions || 1) > 1)))} />
            </DashboardMetricGrid>
          </div>

          <div>
            <div className="text-[11px] font-semibold text-[hsl(var(--ds-muted-fg))] uppercase tracking-wide mb-1">
              ATS Pipeline
            </div>
            <DashboardMetricGrid>
              <StatCard label="Submissions" value={stats.totalSubmissions} color="purple"
                onClick={() => openInsight('Submissions', 'client_apps', applicationColumns, mapApplications(myApplications))} />
              <StatCard label="Submitted" value={stats.submitted} color="blue"
                onClick={() => openInsight('Submitted', 'submitted', applicationColumns, mapApplications(myApplications.filter(a => a.stage === 'submitted')))} />
              <StatCard label="Selected" value={stats.selected} color="cyan"
                onClick={() => openInsight('Selected', 'selected', applicationColumns, mapApplications(myApplications.filter(a => a.stage === 'selected')))} />
              <StatCard label="Not Selected" value={stats.notSelected} color="rose"
                onClick={() => openInsight('Not Selected', 'not_selected', applicationColumns, mapApplications(myApplications.filter(a => a.stage === 'not_selected')))} />
              <StatCard label="In Interview" value={stats.inInterview} color="yellow"
                onClick={() => openInsight('In Interview', 'interview', applicationColumns, mapApplications(myApplications.filter(a => a.stage === 'interview')))} />
              <StatCard label="Shortlisted" value={stats.shortlisted} color="teal"
                onClick={() => openInsight('Shortlisted', 'shortlisted', applicationColumns, mapApplications(myApplications.filter(a => a.stage === 'shortlisted')))} />
              <StatCard label="Offers" value={stats.offers} color="purple"
                onClick={() => openInsight('Offers', 'offer', applicationColumns, mapApplications(myApplications.filter(a => a.stage === 'offer')))} />
              <StatCard label="Joined" value={stats.joined} color="emerald"
                onClick={() => openInsight('Joined Hires', 'client_joined', applicationColumns, mapApplications(myApplications.filter(a => a.stage === 'joined')))} />
              <StatCard label="Rejected" value={stats.rejected} color="red"
                onClick={() => openInsight('Rejected', 'rejected', applicationColumns, mapApplications(myApplications.filter(a => a.stage === 'rejected')))} />
            </DashboardMetricGrid>
          </div>

          <div>
            <div className="text-[11px] font-semibold text-[hsl(var(--ds-muted-fg))] uppercase tracking-wide mb-1">
              Billing
            </div>
            <DashboardMetricGrid>
              <StatCard label="Total Invoices" value={stats.totalInvoices} color="indigo" />
              <StatCard label="Paid Invoices" value={stats.paidInvoices} color="green" />
              <StatCard label="Pending Invoices" value={stats.pendingInvoices} color="amber" />
            </DashboardMetricGrid>
          </div>
        </div>
      </DashboardMetrics>

      <DashboardPanelsGrid columns={2}>
        <DashboardPanel title="Submissions by Stage">
          <BarChart data={stageData} colors={{ submitted: 'bg-[hsl(var(--ds-primary))]', selected: 'bg-cyan-500', not_selected: 'bg-rose-500', interview: 'bg-yellow-500', shortlisted: 'bg-[hsl(var(--ds-primary)/0.75)]', offer: 'bg-purple-500', joined: 'bg-green-500', rejected: 'bg-red-500' }} />
        </DashboardPanel>

        <DashboardPanel title="My Jobs Status">
          <div className="space-y-2">
            {myJobs.length > 0 ? (
              sortByEntityId(myJobs).map(job => (
              <div key={job.id} className="flex items-center justify-between p-2 bg-[hsl(var(--ds-muted))] border border-[hsl(var(--ds-border))]">
                <div className="min-w-0 pr-2">
                  <div className="font-medium text-sm">{job.title}</div>
                  <div className="text-xs text-[hsl(var(--ds-muted-fg))]">{job.location || 'No location'}</div>
                </div>
                <span className={`px-2 py-1 text-xs shrink-0 ${getStatusColor(job.status)}`}>{job.status}</span>
              </div>
            ))
            ) : (
              <div className="text-[hsl(var(--ds-muted-fg))] text-sm">No jobs posted</div>
            )}
          </div>
        </DashboardPanel>
      </DashboardPanelsGrid>
      </>
      )}
    </DashboardPage>
  );
}

function CandidateDashboard() {
  const [insight, setInsight] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: applications } = useQuery({
    queryKey: ['applications'],
    queryFn: () => api.get('/applications').then(r => r.data)
  });
  const { data: profileData } = useQuery({
    queryKey: ['candidateProfile'],
    queryFn: () => api.get('/candidate-profile').then(r => r.data)
  });
  const { data: suitableData } = useQuery({
    queryKey: ['suitableJobs'],
    queryFn: () => api.get('/jobs/suitable-for-me').then(r => r.data),
    retry: false
  });
  const { data: openingsData } = useQuery({
    queryKey: ['currentOpenings'],
    queryFn: () => api.get('/jobs/current-openings-for-me').then(r => r.data),
    retry: false
  });

  const myCandidate = profileData;
  const myApplications = applications || [];
  const suitablePreview = sortByEntityId(suitableData?.jobs || [], row => row.job?.id || row.id).slice(0, 5);
  const suitableTotal = suitableData?.jobs?.length || 0;
  const openingsTotal = openingsData?.jobs?.length || 0;
  const openingsPreview = sortByEntityId(openingsData?.jobs || []).slice(0, 3);
  const skillCount = suitableData?.skillCount ?? (myCandidate?.skills?.length || 0);

  const stats = {
    totalApplications: myApplications.length,
    inReview: myApplications.filter(a => a.stage === 'submitted').length,
    inInterview: myApplications.filter(a => a.stage === 'interview').length,
    shortlisted: myApplications.filter(a => a.stage === 'shortlisted').length,
    offers: myApplications.filter(a => a.stage === 'offer').length,
    joined: myApplications.filter(a => a.stage === 'joined').length,
    rejected: myApplications.filter(a => a.stage === 'rejected').length
  };

  const openInsight = (title, filename, rows) =>
    setInsight({ title, filename, columns: applicationColumns, rows: mapApplications(rows) });

  return (
    <DashboardPage className="portal-candidate-page">
      <InsightModal insight={insight} onClose={() => setInsight(null)} />
      <DashboardHeader title="My Dashboard" />

      {(user?.needsProfileCompletion || myCandidate?.needsProfileCompletion) && (
        <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 px-3 py-2 rounded-xl">
          Please{' '}
          <Link to="/profile" className="text-[hsl(var(--ds-primary))] underline font-medium">
            complete your profile
          </Link>{' '}
          (skills and resume) so recruiters can review you.
        </div>
      )}

      <DashboardMetrics>
        <div className="space-y-2">
          <div>
            <div className="text-[11px] font-semibold text-[hsl(var(--ds-muted-fg))] uppercase tracking-wide mb-1">
              Job Discovery
            </div>
            <DashboardMetricGrid className="md:grid-cols-4 lg:grid-cols-9">
              <StatCard
                label="Suitable Jobs"
                value={suitableTotal}
                color="indigo"
                onClick={() => navigate('/suitable-jobs')}
              />
              <StatCard
                label="Current Openings"
                value={openingsTotal}
                color="green"
                onClick={() => navigate('/suitable-jobs')}
              />
            </DashboardMetricGrid>
          </div>

          <div>
            <div className="text-[11px] font-semibold text-[hsl(var(--ds-muted-fg))] uppercase tracking-wide mb-1">
              My ATS Pipeline
            </div>
            <DashboardMetricGrid className="md:grid-cols-4 lg:grid-cols-9">
              <StatCard label="Applications" value={stats.totalApplications} color="blue"
                onClick={() => openInsight('All Applications', 'my_applications', myApplications)} />
              <StatCard label="Submitted" value={stats.inReview} color="yellow"
                onClick={() => openInsight('In Review', 'review', myApplications.filter(a => a.stage === 'submitted'))} />
              <StatCard label="In Interview" value={stats.inInterview} color="purple"
                onClick={() => openInsight('Interviews', 'interview', myApplications.filter(a => a.stage === 'interview'))} />
              <StatCard label="Shortlisted" value={stats.shortlisted} color="teal"
                onClick={() => openInsight('Shortlisted', 'shortlisted', myApplications.filter(a => a.stage === 'shortlisted'))} />
              <StatCard label="Offers" value={stats.offers} color="green"
                onClick={() => openInsight('Offers', 'offer', myApplications.filter(a => a.stage === 'offer'))} />
              <StatCard label="Joined" value={stats.joined} color="emerald"
                onClick={() => openInsight('Joined', 'joined', myApplications.filter(a => a.stage === 'joined'))} />
              <StatCard label="Rejected" value={stats.rejected} color="red"
                onClick={() => openInsight('Rejected', 'rejected', myApplications.filter(a => a.stage === 'rejected'))} />
            </DashboardMetricGrid>
          </div>
        </div>
      </DashboardMetrics>

      <div className="flex-1 min-h-0 flex flex-col gap-3 overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 flex-1 min-h-0 max-h-[min(48vh,22rem)]">
        <DashboardPanel
          title="Suggested jobs"
          subtitle="Matched to your profile skills"
          action={
            <Link to="/suitable-jobs" className="text-xs text-[hsl(var(--ds-primary))] hover:underline font-medium">
              View all →
            </Link>
          }
        >
          {skillCount === 0 ? (
            <p className="text-sm text-[hsl(var(--ds-warning-fg))] bg-[hsl(var(--ds-warning)/0.12)] border border-[hsl(var(--ds-warning)/0.35)] px-3 py-2">
              Add skills on your{' '}
              <Link to="/profile" className="text-[hsl(var(--ds-primary))] underline font-medium">
                profile
              </Link>{' '}
              to see roles that match you (same skill catalog as job postings).
            </p>
          ) : suitablePreview.length === 0 ? (
            <p className="text-sm text-[hsl(var(--ds-muted-fg))]">
              No open jobs match your skills right now. Check back when recruiters post new roles.
            </p>
          ) : (
            <div className="space-y-2">
              {suitablePreview.map(row => {
                const j = row.job || {};
                const pct = computeMatchPercent(row);
                return (
                  <div
                    key={j.id}
                    className="flex items-center justify-between gap-3 py-2 px-3 border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] rounded-xl"
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{j.title}</div>
                      <div className="text-xs text-[hsl(var(--ds-muted-fg))] truncate">
                        {maskCompanyName(j.clientName)}
                        {j.location ? ` · ${j.location}` : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs font-semibold text-green-800 bg-green-100 px-2 py-0.5">
                        {pct}% match
                      </span>
                      {row.alreadyApplied ? (
                        <span className="text-xs text-[hsl(var(--ds-muted-fg))]">Applied</span>
                      ) : row.canApply ? (
                        <Link
                          to="/suitable-jobs"
                          className="text-xs text-[hsl(var(--ds-primary))] hover:underline font-medium"
                        >
                          Apply
                        </Link>
                      ) : null}
                    </div>
                  </div>
                );
              })}
              {suitableTotal > suitablePreview.length && (
                <p className="text-xs text-[hsl(var(--ds-muted-fg))] text-center pt-1">
                  +{suitableTotal - suitablePreview.length} more on Suitable Jobs
                </p>
              )}
            </div>
          )}
        </DashboardPanel>

        <DashboardPanel
          title="Current openings"
          subtitle="Other open roles — apply or refer"
          action={
            <Link to="/suitable-jobs" className="text-xs text-[hsl(var(--ds-primary))] hover:underline font-medium">
              Browse all →
            </Link>
          }
        >
          {openingsPreview.length === 0 ? (
            <p className="text-sm text-[hsl(var(--ds-muted-fg))]">
              No extra open roles beyond your skill matches, or none posted yet. Browse{' '}
              <Link to="/suitable-jobs" className="text-[hsl(var(--ds-primary))] underline">
                current openings
              </Link>{' '}
              when recruiters add jobs.
            </p>
          ) : (
            <div className="space-y-2">
              {openingsPreview.map(j => {
                return (
                  <div
                    key={j.id}
                    className="flex items-center justify-between gap-3 py-2 px-3 border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] rounded-xl"
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{j.title}</div>
                      <div className="text-xs text-[hsl(var(--ds-muted-fg))] truncate">
                        {maskCompanyName(j.clientName)}
                        {j.location ? ` · ${j.location}` : ''}
                      </div>
                    </div>
                    <Link
                      to="/suitable-jobs"
                      className="text-xs text-[hsl(var(--ds-primary))] hover:underline font-medium shrink-0"
                    >
                      View
                    </Link>
                  </div>
                );
              })}
              {openingsTotal > openingsPreview.length && (
                <p className="text-xs text-[hsl(var(--ds-muted-fg))] text-center pt-1">
                  +{openingsTotal - openingsPreview.length} more openings
                </p>
              )}
            </div>
          )}
        </DashboardPanel>
        </div>

        <DashboardPanel title="My Applications" className="flex-1 min-h-0">
          {myApplications.length > 0 ? (
            <div className="space-y-2">
            {sortByEntityId(myApplications).map(app => (
              <div key={app.id} className="flex items-center justify-between py-3 px-4 border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] rounded-xl">
                <div className="min-w-0 pr-2">
                  <div className="font-medium">{app.jobTitle || app.jobId}</div>
                  <div className="text-sm text-[hsl(var(--ds-muted-fg))]">{app.clientName || 'Unknown Client'}</div>
                  <div className="text-xs text-[hsl(var(--ds-muted-fg))] mt-1">Applied: {new Date(app.createdAt).toLocaleDateString()}</div>
                </div>
                <span className={`px-3 py-1 text-xs font-medium shrink-0 ${getStageColor(app.stage)}`}>{app.stage}</span>
              </div>
            ))}
            </div>
          ) : (
            <div className="text-center py-8 text-[hsl(var(--ds-muted-fg))]">No applications yet</div>
          )}
        </DashboardPanel>
      </div>
    </DashboardPage>
  );
}

function BarChart({ data, colors, labelFormat }) {
  const maxValue = Math.max(...Object.values(data), 1);
  const label = key => (labelFormat ? labelFormat(key) : key.replace(/-/g, ' '));

  return (
    <div className="space-y-1.5">
      {Object.entries(data).map(([key, value]) => (
        <div key={key}>
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-xs font-medium capitalize">{label(key)}</span>
            <span className="text-xs text-[hsl(var(--ds-muted-fg))]">{value}</span>
          </div>
          <div className="w-full bg-[hsl(var(--ds-muted))] h-4">
            <div
              className={`${colors[key] || 'bg-[hsl(var(--ds-primary))]'} h-full flex items-center justify-end pr-1 min-w-0`}
              style={{ width: `${(value / maxValue) * 100}%` }}
            >
              {value > 0 && <span className="text-[10px] text-white font-medium">{value}</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function getStageColor(stage) {
  const colors = {
    submitted: 'bg-[hsl(var(--ds-primary)/0.12)] text-[hsl(var(--ds-primary))]',
    selected: 'bg-cyan-100 text-cyan-800',
    not_selected: 'bg-rose-100 text-rose-800',
    interview: 'bg-yellow-100 text-yellow-700',
    shortlisted: 'bg-[hsl(var(--ds-primary)/0.12)] text-[hsl(var(--ds-primary))]',
    offer: 'bg-purple-100 text-purple-700',
    joined: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700'
  };
  return colors[stage] || 'bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]';
}

function getStatusColor(status) {
  const colors = {
    open: 'bg-green-100 text-green-700',
    'in-progress': 'bg-[hsl(var(--ds-primary)/0.12)] text-[hsl(var(--ds-primary))]',
    'on-hold': 'bg-yellow-100 text-yellow-700',
    closed: 'bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]',
    inactive: 'bg-red-100 text-red-700'
  };
  return colors[status] || 'bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]';
}
